import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';
import { LuChevronDown } from 'react-icons/lu';
import { GLASS_CHIP } from '../ui/glass';
import { useTranslation } from '../../i18n/LanguageContext';

// Form fields of the portal's dialogs (Tailwind, glass style).

// 16px text on phones: iOS zooms into smaller fields on focus. tw-shadow-none
// on bare inputs: legacy dark CSS adds a focus glow to every input.
export const CONTROL_SURFACE = [
  'tw-w-full tw-rounded-xl tw-text-base tw-text-slate-800 tw-outline-none sm:tw-text-sm',
  'placeholder:tw-text-slate-400 focus:tw-ring-2 focus:tw-ring-teal-500/40',
  'disabled:tw-cursor-not-allowed disabled:tw-opacity-60',
  'dark:tw-text-slate-100 dark:placeholder:tw-text-slate-500 dark:[color-scheme:dark]',
  GLASS_CHIP,
].join(' ');

export const ERROR_RING = 'tw-ring-2 tw-ring-rose-500/50 focus:tw-ring-rose-500/60';

export const INPUT_CLASS = `tw-h-10 tw-px-3.5 ${CONTROL_SURFACE}`;

const SELECT_CLASS = `${INPUT_CLASS} tw-cursor-pointer tw-appearance-none tw-pr-9 [&>option]:tw-bg-white [&>option]:tw-text-slate-800 dark:[&>option]:tw-bg-[#1f232b] dark:[&>option]:tw-text-slate-100`;

/** Label, the control, and a hint or an error under it. */
export const Field = ({ label, htmlFor, required, hint, error, badge, className = '', children }) => (
  <div className={`tw-min-w-0 tw-space-y-1.5 ${className}`}>
    <label htmlFor={htmlFor} className="tw-flex tw-min-w-0 tw-items-center tw-gap-1.5 tw-px-0.5 tw-text-xs tw-font-semibold tw-text-slate-600 dark:tw-text-slate-300">
      <span className="tw-truncate">{label}</span>
      {required && <span aria-hidden="true" className="tw-text-rose-500">*</span>}
      {badge}
    </label>
    {children}
    {error && <p role="alert" className="tw-px-0.5 tw-text-xs tw-font-medium tw-text-rose-600 dark:tw-text-rose-400">{error}</p>}
    {!error && hint && <p className="tw-px-0.5 tw-text-xs tw-leading-snug tw-text-slate-500 dark:tw-text-slate-400">{hint}</p>}
  </div>
);

Field.propTypes = {
  label: PropTypes.node.isRequired,
  htmlFor: PropTypes.string,
  required: PropTypes.bool,
  hint: PropTypes.node,
  error: PropTypes.string,
  badge: PropTypes.node,
  className: PropTypes.string,
  children: PropTypes.node,
};

export const TextInput = forwardRef(({ error, className = '', ...rest }, ref) => (
  <input ref={ref} aria-invalid={error ? true : undefined} className={`${INPUT_CLASS} ${error ? ERROR_RING : ''} ${className}`} {...rest} />
));

TextInput.displayName = 'TextInput';
TextInput.propTypes = { error: PropTypes.string, className: PropTypes.string };

export const TextArea = ({ error, className = '', rows = 2, ...rest }) => (
  <textarea
    rows={rows}
    aria-invalid={error ? true : undefined}
    className={`tw-resize-none tw-px-3.5 tw-py-2.5 tw-leading-snug ${CONTROL_SURFACE} ${error ? ERROR_RING : ''} ${className}`}
    {...rest}
  />
);

TextArea.propTypes = { error: PropTypes.string, className: PropTypes.string, rows: PropTypes.number };

/**
 * Native select (best on phones) in the glass style. `current` adds the stored
 * value as an extra "(liste dışı)" option when the list doesn't contain it,
 * so the field shows what is saved instead of "Seçiniz".
 */
export const SelectInput = ({ error, className = '', placeholder, options, current, ...rest }) => {
  const { t } = useTranslation();
  const missing = current !== undefined && current !== null && current !== '' && !options.some((option) => String(option.value) === String(current));
  return (
    <span className="tw-relative tw-block">
      <select aria-invalid={error ? true : undefined} className={`${SELECT_CLASS} ${error ? ERROR_RING : ''} ${className}`} {...rest}>
        {placeholder !== undefined && <option value="">{placeholder}</option>}
        {missing && <option value={current}>{t('dataTable.form.outOfList', { value: String(current) })}</option>}
        {options.map((option) => (
          <option key={String(option.value)} value={option.value}>{option.label}</option>
        ))}
      </select>
      <LuChevronDown aria-hidden="true" className="tw-pointer-events-none tw-absolute tw-right-3 tw-top-1/2 tw-h-4 tw-w-4 -tw-translate-y-1/2 tw-text-slate-400" />
    </span>
  );
};

SelectInput.propTypes = {
  error: PropTypes.string,
  className: PropTypes.string,
  placeholder: PropTypes.string,
  options: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    label: PropTypes.node.isRequired,
  })).isRequired,
  current: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

/** On/off switch shown as a small glass tile. */
export const ToggleTile = ({ id, label, checked, onChange }) => (
  <button
    id={id}
    type="button"
    role="switch"
    aria-checked={checked}
    onClick={() => onChange(!checked)}
    className={`tw-flex tw-h-10 tw-w-full tw-items-center tw-justify-between tw-gap-3 tw-rounded-xl tw-px-3.5 tw-text-sm tw-font-semibold tw-transition-colors focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-teal-500/50 ${GLASS_CHIP} ${
      checked ? 'tw-text-teal-800 dark:tw-text-teal-200' : 'tw-text-slate-600 dark:tw-text-slate-300'
    }`}
  >
    <span className="tw-truncate">{label}</span>
    <span
      aria-hidden="true"
      className={`tw-relative tw-h-5 tw-w-9 tw-shrink-0 tw-rounded-full tw-transition-colors tw-duration-200 ${checked ? 'tw-bg-teal-500' : 'tw-bg-slate-300 dark:tw-bg-white/15'}`}
    >
      <span
        className={`tw-absolute tw-top-0.5 tw-h-4 tw-w-4 tw-rounded-full tw-bg-white tw-shadow-sm tw-transition-[left] tw-duration-200 ${checked ? 'tw-left-[1.125rem]' : 'tw-left-0.5'}`}
      />
    </span>
  </button>
);

ToggleTile.propTypes = {
  id: PropTypes.string,
  label: PropTypes.string.isRequired,
  checked: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
};
